import type React from 'react';
import { createContext, useCallback, useContext } from 'react';

/**
 * Registers the element rendering a given person, or removes it when passed null.
 */
export type RegisterCard = (accountIdentifier: string, element: HTMLElement | null) => void;

const CardFocusRegistryContext = createContext<RegisterCard | null>(null);

export const CardFocusRegistryProvider: React.Provider<RegisterCard | null> =
	CardFocusRegistryContext.Provider;

/**
 * Lets the chart move focus to the card for a specific person without reaching into the DOM.
 *
 * Returns a ref callback for the card's focusable element. Cards are keyed by account identifier
 * rather than by element, because the manager chain renders an unkeyed fragment when collapsed
 * and a keyed array when expanded, so React replaces every card element as the two states swap.
 */
export const useCardFocusRegistration = (
	accountIdentifier?: string | null,
): ((element: HTMLElement | null) => void) => {
	const registerCard = useContext(CardFocusRegistryContext);

	return useCallback(
		(element: HTMLElement | null) => {
			if (registerCard && accountIdentifier) {
				registerCard(accountIdentifier, element);
			}
		},
		[registerCard, accountIdentifier],
	);
};
